import { type JSX, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import { getProfileSubtitle } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { NavTabsContentContainer } from "common/layouts/WorkspaceLayout";

import { CAMPAIGNS_TAB_ITEMS } from "../../constants";
import useBlacklist from "../../datahooks/useBlacklist";
import useBlacklistMutations from "../../datahooks/useBlacklistMutations";
import AddToBlacklistButton from "./AddToBlacklistButton/AddToBlacklistButton";
import BlacklistItem from "./BlacklistItem";

import EmptyStateImage from "assets/images/empty-placeholders/blacklist.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function Blacklist({
  blacklistType,
}: {
  blacklistType: "accounts" | "companies";
}) {
  const isDesktop = useTwBreakpoint("lg");
  const [searchTerm, setSearchTerm] = useState("");
  const { removeFromBlacklist } = useBlacklistMutations();
  const { pathname } = useLocation();
  const { blacklist, isLoadingBlacklist, isBlacklistError, refetchBlacklist } =
    useBlacklist<typeof blacklistType>(blacklistType);

  let mainComponent: JSX.Element;

  if (isBlacklistError) {
    mainComponent = (
      <ErrorPlaceholder
        onRetry={refetchBlacklist}
        errorMessage="Could not display blacklist"
      />
    );
  } else if (isLoadingBlacklist) {
    mainComponent = <Loader />;
  } else if (!blacklist || !blacklist.length) {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={EmptyStateImage}
        title={`No blacklisted ${blacklistType}`}
        subtitle={`Blacklist ${blacklistType} to exclude them from all current and future campaigns`}
        actionComponent={
          <AddToBlacklistButton
            type={blacklistType}
            size="md"
            isEmptyPlaceholderButton
          />
        }
      />
    );
  } else {
    const filteredBlacklist = blacklist.filter((blacklistItem) => {
      const string =
        "full_name" in blacklistItem
          ? blacklistItem.full_name
          : blacklistItem.name;
      return string.toLowerCase().includes(searchTerm.toLowerCase());
    });
    mainComponent =
      filteredBlacklist.length === 0 ? (
        <EmptyPlaceholder
          imageSrc={NoResultsImage}
          title={`No ${blacklistType} found`}
          subtitle={`No ${blacklistType} match your search criteria. Try adjusting your search`}
          actionComponent={
            <Button onClick={() => setSearchTerm("")}>Clear search</Button>
          }
        />
      ) : (
        <section className="grid grid-cols-1 grid-rows-1 gap-2.5 lg:grid-cols-2 lg:gap-3">
          {filteredBlacklist.map((blacklistItem) => {
            if ("full_name" in blacklistItem) {
              const { id, picture_url, full_name, urn } = blacklistItem;
              return (
                <BlacklistItem
                  key={id}
                  title={full_name}
                  subtitle={getProfileSubtitle(blacklistItem)}
                  pictureUrl={picture_url}
                  onRemove={() => removeFromBlacklist({ accountUrn: urn })}
                />
              );
            }

            const { name, urn, description, logo_url } = blacklistItem;
            return (
              <BlacklistItem
                key={urn}
                title={name}
                pictureUrl={logo_url}
                subtitle={description}
                onRemove={() => removeFromBlacklist({ companyUrn: urn })}
              />
            );
          })}
        </section>
      );
  }

  const primaryActionsComponent = (
    <RenderIf condition={!!blacklist?.length}>
      <AddToBlacklistButton
        type={blacklistType}
        size={isDesktop ? "sm" : "md"}
        isEmptyPlaceholderButton={false}
      />
    </RenderIf>
  );

  const isCompanies = pathname.endsWith("companies");

  return (
    <NavTabsContentContainer
      primaryActionsComponent={primaryActionsComponent}
      tabItems={CAMPAIGNS_TAB_ITEMS}
    >
      <div className="flex gap-x-2">
        <div className="flex rounded-full p-1 gap-x-1 bg-black-100">
          <Button
            asChild
            size="xs"
            variant={!isCompanies ? "outline-black" : "ghost-black"}
          >
            <NavLink to="../blacklist">Accounts</NavLink>
          </Button>
          <Button
            asChild
            size="xs"
            variant={isCompanies ? "outline-black" : "ghost-black"}
          >
            <NavLink to="../blacklist/companies">Companies</NavLink>
          </Button>
        </div>
        <RenderIf condition={!!blacklist?.length}>
          <SearchInput
            placeholder={`Search ${blacklistType}`}
            value={searchTerm}
            className="lg:max-w-80"
            variant={isDesktop ? "sm" : "lg"}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
          />
        </RenderIf>
      </div>
      {mainComponent}
    </NavTabsContentContainer>
  );
}
